import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/request-password-reset", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto px-5 py-16" data-testid="forgot-password-sent">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Check your email</div>
        <h1 className="font-display text-4xl mt-2">Reset link sent</h1>
        <div className="mt-6 rounded-md bg-[#D4DEC6]/30 border border-[#2B3A67]/20 p-5 text-sm text-[#1A1F2C] leading-relaxed">
          <div className="flex items-center gap-2 text-[#2B3A67] font-semibold mb-2">
            <CheckCircle2 size={18} /> Email on its way
          </div>
          If an account with <strong>{email}</strong> exists, a password-reset link has been sent from <em>official@locanest.in</em>.
          The link expires in <strong>30 minutes</strong> and can be used only once.
          <div className="mt-3 text-xs text-[#5C6B7A]">
            Not seeing it? Check your spam folder, or wait a couple of minutes for delivery.
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-2 text-sm">
          <button
            type="button"
            onClick={() => { setSent(false); setEmail(""); }}
            className="text-[#C85A32] font-medium text-left"
            data-testid="forgot-password-resend"
          >
            Send to a different email
          </button>
          <Link to="/login" className="text-[#5C6B7A] hover:text-[#1A1F2C]" data-testid="forgot-back-to-login">
            ← Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Password recovery</div>
      <h1 className="font-display text-4xl mt-2">Forgot password?</h1>
      <p className="text-[#5C6B7A] mt-3 leading-relaxed">
        Enter the email address on your LocaNest account. We&rsquo;ll email you a secure link to set a new password.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-password-form">
        <div>
          <Label htmlFor="fp-email">Email</Label>
          <Input
            id="fp-email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-email"
          />
        </div>
        {error && <div className="text-sm text-red-600" data-testid="forgot-error">{error}</div>}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11"
          data-testid="forgot-submit"
        >
          {loading ? "Sending…" : "Send reset link"}
        </Button>
      </form>

      <div className="mt-6 text-sm text-[#5C6B7A]">
        Remembered it? <Link to="/login" className="text-[#C85A32] font-medium" data-testid="forgot-back-to-login">Back to login</Link>
      </div>
    </div>
  );
}
